// The single source for user-facing bracket vocabulary: the number, the name
// players use, a short strip label, the turn range, and the brand color.
//
// This exists because the same five rows were duplicated in AboutPage and
// BracketPrimer, and the build page needed a third. The turn strings must
// stay in lockstep with the API's bracket_thresholds.json and the About page -
// that pairing is a hard project rule, so there is exactly one copy of it here.
//
// The English strings below are the translation SOURCE and the fallback;
// screens render them through bracketMetaLabel() (common:bracket_meta).
import { tc } from '../i18n'

export interface BracketMeta {
  bracket: number
  /** Display name. 5 stays "cEDH" because that is what the community says. */
  name: string
  /**
   * WotC's official name for the tier. Distinct from `name`: a surface that
   * already shows the digit wants this word, not "Bracket 1" beside a 1.
   * These strings are also spelled out in CheckerPage's FAQ copy and in
   * accuracySnapshot's per-bracket labels; this is the canonical copy and
   * those should converge on it rather than the reverse.
   */
  wotc: string
  /** Compact label for strips and pills. */
  short: string
  /** Turn range, as shown on /about. */
  turns: string
  hex: string
}

export const BRACKET_META: BracketMeta[] = [
  { bracket: 1, name: 'Bracket 1', wotc: 'Exhibition', short: '1', turns: '10+ turns', hex: '#22c55e' },
  { bracket: 2, name: 'Bracket 2', wotc: 'Core', short: '2', turns: '8+ turns', hex: '#3b82f6' },
  { bracket: 3, name: 'Bracket 3', wotc: 'Upgraded', short: '3', turns: '6+ turns', hex: '#a855f7' },
  { bracket: 4, name: 'Bracket 4', wotc: 'Optimized', short: '4', turns: '4+ turns', hex: '#f97316' },
  { bracket: 5, name: 'cEDH', wotc: 'cEDH', short: 'cEDH', turns: 'Any turn', hex: '#ef4444' },
]

export function bracketMeta(n: number): BracketMeta | undefined {
  return BRACKET_META.find(b => b.bracket === n)
}

/** The text fields of a bracket row, as rendered. */
export type BracketMetaField = 'name' | 'wotc' | 'short' | 'turns'

/**
 * A bracket row's display text in the active language:
 * `common:bracket_meta.<n>.<field>`. The English catalog holds the same
 * strings as BRACKET_META (pinned by bracketMeta.test.ts), and the row's own
 * English is the fallback for a bracket the catalog does not know.
 */
export function bracketMetaLabel(b: BracketMeta, field: BracketMetaField): string {
  return tc(`bracket_meta.${b.bracket}.${field}`, { defaultValue: b[field] })
}
